import React, { useRef, useState, useEffect, useCallback } from "react";
import { throttle } from "lodash";
import { any, array, bool, number } from "prop-types";
import { useDeviceType } from "../../customHooks/useDeviceType";

import ArrowDashIcon from "../Arrow-DashIcon";

import "./carousel.m.css";

const Carousel = ({
  children,
  itemWidth = 200,
  gap = 16,
  showDots = true,
  showDesktopDots = false,
  showButtons = false,
  maxItemWidth,
  itemMobileWidth,
}) => {
  const carouselRef = useRef(null);
  const isMobile = useDeviceType() !== "desktop";
  const itemWidthFinal = isMobile && itemMobileWidth ? itemMobileWidth : itemWidth;
  const itemWidthFinalWithGap = isMobile ? itemWidthFinal + gap : itemWidthFinal + gap + gap;
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(1);
  const childrenArray = React.Children.toArray(children);
  const isLastIndex = !isMobile && showButtons ? childrenArray.length - visibleCards : childrenArray.length - 1;

  const throttledSetValue = useCallback(
    throttle((newValue) => {
      setActiveIndex(newValue);
    }, 350),
    []
  );

  const handleScroll = () => {
    const element = carouselRef.current;
    if (!element) return;

    const scrollLeft = element.scrollLeft;
    const scrollRight = element.scrollWidth - element.clientWidth - scrollLeft;
    const index = Math.round(scrollLeft / itemWidthFinal + 0.1);

    if (showButtons) {
      const indexFromRight = scrollRight / itemWidthFinal;
      const finalIndex = isMobile && indexFromRight < 0.2 ? childrenArray.length - 1 : index;
      throttledSetValue(finalIndex);
    } else {
      throttledSetValue(index);
    }
  };

  const goToSlide = (index) => {
    if (carouselRef.current) {
      const unitWidth = showButtons ? itemWidthFinalWithGap : itemWidthFinal;
      carouselRef.current.scrollTo({
        left: index * unitWidth,
        behavior: "smooth",
      });
      throttledSetValue(index);
    }
  };

  useEffect(() => {
    const calculateVisibleCards = () => {
      if (carouselRef.current) {
        const containerWidth = carouselRef.current.offsetWidth;
        const count = Math.floor(containerWidth / itemWidthFinal);
        setVisibleCards(count);
      }
    };
    showButtons && calculateVisibleCards();
    window.addEventListener("resize", calculateVisibleCards);
    return () => window.removeEventListener("resize", calculateVisibleCards);
  }, [itemWidthFinal, showButtons]);

  useEffect(() => {
    const ref = carouselRef.current;
    if (ref) {
      ref.addEventListener("scroll", handleScroll);
      return () => ref.removeEventListener("scroll", handleScroll);
    }
  }, [itemWidthFinal, isMobile]);

  return (
    <div styleName="carousel-container" className="carousel-container">
      <div
        styleName="carousel"
        className="carousel"
        ref={carouselRef}
        style={{
          gap: `${gap}px`,
        }}
      >
        {!isMobile && showButtons && (
          <>
            {activeIndex !== 0 && (
              <div className="nav-back-button" styleName="nav-button back" onClick={() => goToSlide(activeIndex - 1)}>
                <span styleName="svg-back">
                  {/* <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" /> */}
                </span>
              </div>
            )}
            {activeIndex !== isLastIndex && (
              <div className="nav-next-button" styleName="nav-button next" onClick={() => goToSlide(activeIndex + 1)}>
                <span styleName="svg-next">
                  {/* <ArrowDashIcon height="26" width="26" viewBox="0 0 26 26" /> */}
                </span>
              </div>
            )}
          </>
        )}
        {childrenArray.map((child, index) => (
          <div
            key={index}
            styleName="carousel-item"
            className="carousel-item"
            style={{
              minWidth: `${itemWidthFinal}px`,
              maxWidth: isMobile && itemMobileWidth ? itemMobileWidth : maxItemWidth || "unset",
            }}
          >
            {child}
          </div>
        ))}
      </div>
      {showDots && (
        <div styleName={showDesktopDots ? "carousel-desktop-dots" : `carousel-dots`}>
          {childrenArray.map((_, index) => (
            <span
              key={index}
              styleName={`carousel-dot ${index === activeIndex ? "active" : ""}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

Carousel.propTypes = {
  children: any,
  items: array,
  itemWidth: number,
  itemMobileWidth: number,
  gap: number,
  showDots: bool,
  showDesktopDots: bool,
  showButtons: bool,
  maxItemWidth: bool,
};

export default Carousel;
